import { useTranslation } from "react-i18next";
import { IDashboardStats } from "../../models/DashboardStats";
import DashboardTile from "./DashboardTile";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

interface IVehicleProcessingTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type ProcessingRow = {
  key: keyof IDashboardStats;
  todayKey: keyof IDashboardStats;
  label: string;
  bgClass: string;
  textClass: string;
};

// Every row below reads real API fields. Until now this table was typed against a local
// `IDashboardStats & { vehiclesAwaitingDepollution?: number; ... }` cast whose fields existed
// nowhere in the API, so every cell rendered an em-dash. The "Awaiting Crushing" row is gone
// rather than fixed: there is no crushing timestamp anywhere in the schema, and a permanently
// empty row is worse than no row.
const depollutionRow: ProcessingRow = {
  key: "vehiclesAwaitingDepollution",
  todayKey: "vehiclesDepollutedToday",
  label: "Awaiting Depollution",
  bgClass: "bg-blue-50",
  textClass: "text-blue-700",
};

const triageRow: ProcessingRow = {
  key: "vehiclesAwaitingTriage",
  todayKey: "vehiclesTriagedToday",
  label: "Awaiting Triage",
  bgClass: "bg-emerald-50",
  textClass: "text-emerald-700",
};

const dismantlingRow: ProcessingRow = {
  key: "vehiclesAwaitingDismantling",
  todayKey: "vehicleDismantledToday",
  label: "Awaiting Dismantling",
  bgClass: "bg-violet-50",
  textClass: "text-violet-700",
};

function formatValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

export default function VehicleProcessingTable({ dashboardStats, span = 2 }: IVehicleProcessingTableProps) {
  const { t } = useTranslation("dashboard");

  if (!dashboardStats) return null;

  const hasTriage = CheckFeatureFlag("Triage");

  // When Triage is enabled, surface it alongside Depollution.
  const rows: ProcessingRow[] = hasTriage
    ? [depollutionRow, triageRow, dismantlingRow]
    : [depollutionRow, dismantlingRow];

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">{t("Vehicle Processing")}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{t("Vehicles awaiting processing vs processed today")}</p>
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">{t("Metric")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Awaiting Processing")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Processed Today")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, todayKey, label, bgClass, textClass }) => {
                const value = dashboardStats[key] as number | null | undefined;
                const todayValue = dashboardStats[todayKey] as number | null | undefined;
                return (
                  <tr key={String(key)} className={`font-medium transition-colors ${bgClass}`}>
                    <td className={`py-2.5 pl-4 pr-4 ${textClass}`}>{t(label)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(value)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(todayValue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
